/**
 * ThruChat Dashboard Theme Constants
 *
 * Centralized constants for consistent spacing, sizing, and styling
 * throughout the application. Use these instead of magic numbers.
 */

/**
 * Spacing constants (in pixels)
 * Based on 8px grid system - all spacing should be multiples of 8
 */
export const SPACING = {
  // Extra small
  XS: 4, // 4px
  XS2: 8, // 8px (base unit)

  // Small
  SM: 12, // 12px
  SM2: 16, // 16px

  // Medium
  MD: 20, // 20px
  MD2: 24, // 24px

  // Large
  LG: 28, // 28px
  LG2: 32, // 32px

  // Extra large
  XL: 40, // 40px
  XL2: 48, // 48px

  // Huge
  XXL: 56, // 56px
  XXL2: 64, // 64px
} as const;

/**
 * Icon sizes (in pixels)
 * Standardized icon dimensions for consistent visual hierarchy
 */
export const ICON_SIZE = {
  // Small icons (table cells, chips, inline elements)
  XS: 16,
  SM: 20,

  // Medium icons (buttons, list items)
  MD: 24,
  LG: 28,

  // Large icons (card headers, section headers)
  XL: 40,
  XXL: 48,

  // Hero icons (large metric cards, empty states)
  HERO: 56,
  MEGA: 64,
} as const;

/**
 * Border radius constants (in pixels)
 * Consistent corner rounding throughout the application
 */
export const BORDER_RADIUS = {
  // Sharp corners
  NONE: 0,

  // Subtle rounding
  XS: 4,
  SM: 6,
  MD: 8, // Default Material-UI theme value

  // Medium rounding (chips, buttons)
  LG: 12,

  // Large rounding (cards, dialogs)
  XL: 16,

  // Extra large (special cases)
  XXL: 24,

  // Pill shape
  PILL: 999,
} as const;

/**
 * Component-specific dimensions
 * Standardized sizes for common UI elements
 */
export const DIMENSIONS = {
  // Icon boxes (gradient containers for icons)
  ICON_BOX: {
    SMALL: 36,
    MEDIUM: 40,
    LARGE: 56,
  },

  // Avatar sizes
  AVATAR: {
    SMALL: 32,
    MEDIUM: 40,
    LARGE: 56,
  },

  // Button heights
  BUTTON: {
    SMALL: 32,
    MEDIUM: 38,
    LARGE: 44,
  },

  // Input heights
  INPUT: {
    SMALL: 36,
    MEDIUM: 38,
    LARGE: 42,
  },

  // Minimum widths for form controls
  MIN_WIDTH: {
    SELECT: 140,
    SEARCH: 200,
    DIALOG: 220,
  },

  // Card content padding
  CARD_PADDING: 24, // 3 * theme.spacing(8px)

  // Table minimum width
  TABLE_MIN_WIDTH: 650,

  // Dialog minimum dimensions
  DIALOG: {
    MIN_HEIGHT: 240,
    MAX_WIDTH_SM: 400,
    MAX_WIDTH_MD: 500,
  },

  // Empty state max width
  EMPTY_STATE_MAX_WIDTH: 400,

  // Skeleton heights
  SKELETON: {
    TEXT: 20,
    BUTTON: 32,
    CHART: 300,
    CARD: 160,
  },
} as const;

/**
 * Animation durations (in milliseconds)
 * Standardized timing for transitions and animations
 */
export const ANIMATION = {
  // Fast micro-interactions
  FAST: 100,

  // Standard transitions
  NORMAL: 200,

  // Slower state changes
  SLOW: 300,

  // Complex animations
  COMPLEX: 500,
} as const;

/**
 * Z-index layers
 * Consistent stacking context throughout the application
 */
export const Z_INDEX = {
  DRAWER: 1200,
  APP_BAR: 1100,
  MODAL: 1300,
  SNACKBAR: 1400,
  TOOLTIP: 1500,
  FAB: 1050,
} as const;

/**
 * Typography sizes (in rem)
 * Consistent font sizes beyond theme.typography variants
 */
export const FONT_SIZE = {
  XS: "0.625rem", // 10px
  SM: "0.75rem", // 12px
  MD: "0.875rem", // 14px (body2)
  LG: "1rem", // 16px (body1)
  XL: "1.25rem", // 20px (h6)
  XXL: "1.5rem", // 24px (h5)
  HERO: "2rem", // 32px (h3)
  MEGA: "2.5rem", // 40px (h2)
  GIGA: "3rem", // 48px
} as const;

/**
 * Line heights for custom typography
 */
export const LINE_HEIGHT = {
  TIGHT: 1.2,
  SNUG: 1.3,
  NORMAL: 1.5,
  RELAXED: 1.7,
  LOOSE: 1.8,
} as const;

/**
 * Common color opacity values
 * For consistent transparency across components
 */
export const OPACITY = {
  DISABLED: 0.38,
  HOVER: 0.04,
  SELECTED: 0.08,
  FOCUS: 0.12,
  DIVIDER: 0.12,
  OVERLAY_LIGHT: 0.2, // For skeleton on gradient backgrounds
  OVERLAY_DARK: 0.1, // For overlays on light backgrounds
  SEMI_TRANSPARENT: 0.9,
} as const;

/**
 * Progress bar heights
 */
export const PROGRESS_HEIGHT = {
  THIN: 3,
  DEFAULT: 6,
  THICK: 8,
} as const;

/**
 * Scrollbar dimensions
 */
export const SCROLLBAR = {
  WIDTH: 6,
  BORDER_RADIUS: 3,
} as const;

/**
 * Helper function to convert spacing constant to theme spacing
 * Usage: sx={{ p: getSpacing(SPACING.MD2) }} // p: 3 (24px)
 */
export const getSpacing = (pixels: number): number => {
  return pixels / 8; // Convert to theme spacing units
};

/**
 * Helper function to convert pixel value to rem
 */
export const pxToRem = (pixels: number): string => {
  return `${pixels / 16}rem`;
};

/**
 * Common sx prop patterns
 * Reusable style objects for consistent patterns
 */
export const SX_PATTERNS = {
  // Card hover effect
  CARD_HOVER: {
    transition: `transform ${ANIMATION.NORMAL}ms ease-in-out, box-shadow ${ANIMATION.NORMAL}ms ease-in-out`,
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: 6,
    },
  },

  // Centered flex container
  CENTER: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  // Flex row with gap
  FLEX_ROW: (gap: number = SPACING.SM2) => ({
    display: "flex",
    alignItems: "center",
    gap: getSpacing(gap),
  }),

  // Flex column with gap
  FLEX_COLUMN: (gap: number = SPACING.SM2) => ({
    display: "flex",
    flexDirection: "column",
    gap: getSpacing(gap),
  }),

  // Smooth transition
  TRANSITION: (properties: string[] = ["all"]) => ({
    transition: properties
      .map((prop) => `${prop} ${ANIMATION.NORMAL}ms ease-in-out`)
      .join(", "),
  }),

  // Gradient text effect
  GRADIENT_TEXT: (gradient: string) => ({
    background: gradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  }),

  // Truncate text with ellipsis
  TRUNCATE: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  // Scrollbar styling
  CUSTOM_SCROLLBAR: {
    "&::-webkit-scrollbar": {
      width: SCROLLBAR.WIDTH,
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(0, 0, 0, 0.2)",
      borderRadius: SCROLLBAR.BORDER_RADIUS,
      "&:hover": {
        background: "rgba(0, 0, 0, 0.3)",
      },
    },
  },
} as const;
